import React, { useState, useRef, useEffect } from 'react';
import './AIAssistant.css';
import { useAppStore } from '../state/AppStore';

function AIAssistant() {
  // Контекст текущего шага — то, что в будущем получит AI, чтобы вести по шагам
  // и давать советы (какой блок открыт, что уже введено, история диалога).
  const { state, currentBlock } = useAppStore();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      type: 'assistant',
      text: 'Привет! Я Mike AI, твой помощник. Могу помочь с вопросами по работе с конфликтами. Задай мне вопрос!',
      timestamp: Date.now()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [imageError, setImageError] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  const avatarSrc = `${process.env.PUBLIC_URL || ''}/mikeai.png`;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
      // Фокус на поле ввода при открытии
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen, messages]);

  // Клик по *термину* в тексте экрана → открыть чат и задать вопрос про термин.
  useEffect(() => {
    if (!state.assistantNonce) return;
    const term = state.assistantTerm;
    setIsOpen(true);

    // Зашитый ответ из глоссария — показываем сразу, без обращения к AI.
    if (state.assistantAnswer) {
      setMessages((prev) => [
        ...prev,
        { type: 'user', text: `Что такое «${term}»?`, timestamp: Date.now() },
        { type: 'assistant', text: state.assistantAnswer, timestamp: Date.now() },
      ]);
      return;
    }

    // Иначе — вопрос к AI (пока заглушка).
    const question = state.assistantQuestion || `Объясни: «${term}»`;
    setMessages((prev) => [
      ...prev,
      { type: 'user', text: question, timestamp: Date.now() },
    ]);
    const timer = setTimeout(() => {
      setMessages((prev) => [
        ...prev,
        {
          type: 'assistant',
          text: `Скоро отвечу про «${term}» с учётом текущего шага. (AI-эндпоинт ещё не подключён.)`,
          timestamp: Date.now(),
        },
      ]);
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.assistantNonce]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleSend = (e) => {
    e.preventDefault();
    if (!inputValue.trim()) return;

    // Добавляем сообщение пользователя
    const userMessage = {
      type: 'user',
      text: inputValue.trim(),
      timestamp: Date.now()
    };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');

    // Контекст шага для будущего AI-эндпоинта (вести по шагам + давать советы).
    // eslint-disable-next-line no-unused-vars
    const stepContext = {
      currentBlockId: state.currentBlockId,
      currentBlockText: currentBlock ? currentBlock.text : null,
      variables: state.variables,
      history: state.history,
    };

    // TODO: отправить { message, stepContext } на AI-эндпоинт и показать ответ.
    // Пока что просто добавляем заглушку ответа.
    setTimeout(() => {
      const assistantMessage = {
        type: 'assistant',
        text: 'Спасибо за вопрос! Функциональность отправки сообщений будет добавлена в следующем этапе.',
        timestamp: Date.now()
      };
      setMessages(prev => [...prev, assistantMessage]);
    }, 500);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(e);
    }
  };

  return (
    <>
      {/* Кнопка помощника */}
      <button 
        className={`ai-assistant-button ${isOpen ? 'open' : ''}`}
        onClick={handleToggle}
        aria-label="Открыть чат с Mike AI"
      >
        <div className="ai-assistant-avatar">
          {!imageError ? (
            <img 
              src={avatarSrc} 
              alt="Mike AI"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="ai-assistant-avatar-fallback">M</div>
          )}
        </div>
        <span className="ai-assistant-name">Mike AI</span>
        {!isOpen && <span className="ai-assistant-badge">1</span>}
      </button>

      {/* Диалоговое окно */}
      {isOpen && (
        <div className="ai-assistant-dialog">
          <div className="ai-assistant-dialog-header">
            <div className="ai-assistant-dialog-avatar">
              {!imageError ? (
                <img 
                  src={avatarSrc} 
                  alt="Mike AI"
                  onError={() => setImageError(true)}
                />
              ) : (
                <div className="ai-assistant-avatar-fallback">M</div>
              )}
            </div>
            <div className="ai-assistant-dialog-info">
              <h3 className="ai-assistant-dialog-name">Mike AI</h3>
              <span className="ai-assistant-dialog-status">Онлайн</span>
            </div>
            <button 
              className="ai-assistant-dialog-close"
              onClick={handleToggle}
              aria-label="Закрыть чат"
            >
              ×
            </button>
          </div>

          <div className="ai-assistant-dialog-messages">
            {messages.map((message, index) => (
              <div 
                key={index} 
                className={`ai-assistant-message ${message.type === 'user' ? 'user' : 'assistant'}`}
              >
                {message.type === 'assistant' && (
                  <div className="ai-assistant-message-avatar">
                    {!imageError ? (
                      <img 
                        src={avatarSrc} 
                        alt="Mike AI"
                        onError={() => setImageError(true)}
                      />
                    ) : (
                      <div className="ai-assistant-avatar-fallback ai-assistant-avatar-fallback-small">M</div>
                    )}
                  </div>
                )}
                <div className="ai-assistant-message-content">
                  <p>{message.text}</p>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          <form className="ai-assistant-dialog-input-form" onSubmit={handleSend}>
            <textarea
              ref={inputRef}
              className="ai-assistant-dialog-input"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Напишите сообщение..."
              rows="1"
            />
            <button 
              type="submit" 
              className="ai-assistant-dialog-send"
              disabled={!inputValue.trim()}
              aria-label="Отправить сообщение"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22 2L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </form>
        </div>
      )}
    </>
  );
}

export default AIAssistant;

